import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { HeartPulse, LogOut, Inbox, Briefcase, FileText, Users, Plus, Pencil, Trash2, Loader2, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const STATUS = ["new", "contacted", "converted", "closed"];
const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [posts, setPosts] = useState([]);

  const load = useCallback(async () => {
    const [l, a, j, p] = await Promise.all([
      api.get("/admin/leads"), api.get("/admin/applications"), api.get("/admin/jobs"), api.get("/admin/blog"),
    ]);
    setLeads(l.data); setApps(a.data); setJobs(j.data); setPosts(p.data);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Failed to load data")); }, [load]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const updateLeadStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    toast.success("Status updated");
  };
  const deleteLead = async (id) => { await api.delete(`/admin/leads/${id}`); setLeads((ls) => ls.filter((l) => l.id !== id)); toast.success("Lead deleted"); };

  const newLeads = leads.filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))]">
      <header className="bg-primary text-primary-foreground sticky top-0 z-40">
        <div className="container-page h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary-foreground/15"><HeartPulse className="w-6 h-6" /></span>
            <div><p className="font-heading text-2xl leading-none">Esprit Dashboard</p><p className="text-sm text-primary-foreground/70">{user?.email}</p></div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-2 text-primary-foreground/90 hover:text-primary-foreground"><ExternalLink className="w-5 h-5" /> View Site</a>
            <button data-testid="admin-logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 px-5 h-11 font-semibold transition-colors"><LogOut className="w-5 h-5" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container-page py-10">
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "New Leads", value: newLeads, icon: Inbox, accent: true },
            { label: "Total Leads", value: leads.length, icon: Users },
            { label: "Applications", value: apps.length, icon: FileText },
            { label: "Open Jobs", value: jobs.filter((j) => j.active).length, icon: Briefcase },
          ].map((s) => (
            <div key={s.label} className={`rounded-2xl border p-6 ${s.accent ? "bg-accent/15 border-accent" : "bg-card border-border"}`}>
              <s.icon className="w-7 h-7 text-primary" />
              <p className="font-heading text-4xl text-primary mt-3">{s.value}</p>
              <p className="text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="bg-card border border-border h-auto p-1.5 flex-wrap">
            <TabsTrigger value="leads" data-testid="tab-leads" className="text-base px-5 py-2">Leads</TabsTrigger>
            <TabsTrigger value="apps" data-testid="tab-apps" className="text-base px-5 py-2">Applications</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs" className="text-base px-5 py-2">Jobs</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="text-base px-5 py-2">Blog</TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6">
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-muted text-sm uppercase tracking-wide text-muted-foreground">
                    <tr>{["Name", "Contact", "Service", "Status", "Date", ""].map((h) => <th key={h} className="px-5 py-4 font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {leads.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No leads yet.</td></tr>}
                    {leads.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} className="align-top">
                        <td className="px-5 py-4"><p className="font-semibold">{l.name}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {l.zip && <span className="text-xs rounded-full bg-primary/10 text-primary px-2 py-0.5">ZIP {l.zip}</span>}
                            {l.heard_about && <span className="text-xs rounded-full bg-muted px-2 py-0.5">via {l.heard_about}</span>}
                          </div>
                          {l.message && <p className="text-sm text-muted-foreground max-w-xs mt-1">{l.message}</p>}</td>
                        <td className="px-5 py-4 text-sm"><a href={`mailto:${l.email}`} className="text-primary block">{l.email}</a><a href={`tel:${l.phone}`} className="text-muted-foreground">{l.phone}</a></td>
                        <td className="px-5 py-4 text-sm">{l.service || "—"}</td>
                        <td className="px-5 py-4">
                          <Select value={l.status} onValueChange={(v) => updateLeadStatus(l.id, v)}>
                            <SelectTrigger data-testid={`lead-status-${l.id}`} className="w-36 h-10"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4 text-sm text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</td>
                        <td className="px-5 py-4"><button onClick={() => deleteLead(l.id)} className="text-destructive hover:bg-destructive/10 rounded-lg p-2"><Trash2 className="w-5 h-5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* APPLICATIONS */}
          <TabsContent value="apps" className="mt-6">
            <div className="bg-card rounded-2xl border border-border overflow-hidden overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-muted text-sm uppercase tracking-wide text-muted-foreground">
                  <tr>{["Applicant", "Position", "Contact", "Note", "Date"].map((h) => <th key={h} className="px-5 py-4 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {apps.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No applications yet.</td></tr>}
                  {apps.map((a) => (
                    <tr key={a.id} data-testid={`app-row-${a.id}`} className="align-top">
                      <td className="px-5 py-4 font-semibold">{a.name}</td>
                      <td className="px-5 py-4 text-sm">{a.job_title || "—"}</td>
                      <td className="px-5 py-4 text-sm"><a href={`mailto:${a.email}`} className="text-primary block">{a.email}</a><span className="text-muted-foreground">{a.phone}</span></td>
                      <td className="px-5 py-4 text-sm text-muted-foreground max-w-xs">{a.message || "—"}</td>
                      <td className="px-5 py-4 text-sm text-muted-foreground whitespace-nowrap">{fmt(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* JOBS */}
          <TabsContent value="jobs" className="mt-6">
            <JobsManager jobs={jobs} reload={load} />
          </TabsContent>

          {/* BLOG */}
          <TabsContent value="blog" className="mt-6">
            <BlogManager posts={posts} reload={load} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ---------- Jobs Manager ---------- */
const emptyJob = { title: "", location: "", employment_type: "Full-time", department: "", summary: "", description: "", requirements: "", active: true };

function JobsManager({ jobs, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyJob);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing(null); setForm(emptyJob); setOpen(true); };
  const openEdit = (j) => { setEditing(j); setForm({ ...j, requirements: (j.requirements || []).join("\n") }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, requirements: form.requirements.split("\n").map((r) => r.trim()).filter(Boolean) };
      if (editing) await api.put(`/admin/jobs/${editing.id}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success(editing ? "Job updated" : "Job created");
      setOpen(false); await reload();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/admin/jobs/${id}`); toast.success("Job deleted"); reload(); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-heading text-2xl text-primary">Job Listings</h2>
        <button data-testid="add-job-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground h-11 px-5 font-semibold"><Plus className="w-5 h-5" /> New Job</button>
      </div>
      <div className="space-y-3">
        {jobs.length === 0 && <p className="text-muted-foreground">No jobs yet.</p>}
        {jobs.map((j) => (
          <div key={j.id} data-testid={`job-manage-${j.id}`} className="bg-card rounded-2xl border border-border p-5 flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{j.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${j.active ? "bg-secondary/20 text-secondary" : "bg-muted text-muted-foreground"}`}>{j.active ? "Active" : "Hidden"}</span>
              </div>
              <p className="text-sm text-muted-foreground">{j.location} · {j.employment_type}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(j)} className="rounded-lg p-2 hover:bg-muted"><Pencil className="w-5 h-5" /></button>
              <button onClick={() => del(j.id)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="w-5 h-5" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit Job" : "New Job"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="job-title-input" value={form.title} onChange={set("title")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input value={form.location} onChange={set("location")} /></div>
              <div><Label>Department</Label><Input value={form.department || ""} onChange={set("department")} /></div>
            </div>
            <div><Label>Employment Type</Label><Input value={form.employment_type} onChange={set("employment_type")} /></div>
            <div><Label>Summary</Label><Input value={form.summary} onChange={set("summary")} /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} className="min-h-[100px]" /></div>
            <div><Label>Requirements (one per line)</Label><Textarea value={form.requirements} onChange={set("requirements")} className="min-h-[80px]" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} /><Label>Active (visible on site)</Label></div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-job-btn" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground h-12 px-6 font-semibold disabled:opacity-60">{saving && <Loader2 className="w-5 h-5 animate-spin" />} Save Job</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Blog Manager ---------- */
const emptyPost = { title: "", excerpt: "", content: "", cover_image: "", author: "Esprit Home Healthcare", tags: "", published: true };

function BlogManager({ posts, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing(null); setForm(emptyPost); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, tags: (p.tags || []).join(", ") }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (editing) await api.put(`/admin/blog/${editing.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(editing ? "Post updated" : "Post created");
      setOpen(false); await reload();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/admin/blog/${id}`); toast.success("Post deleted"); reload(); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-heading text-2xl text-primary">Blog Posts</h2>
        <button data-testid="add-post-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground h-11 px-5 font-semibold"><Plus className="w-5 h-5" /> New Post</button>
      </div>
      <div className="space-y-3">
        {posts.length === 0 && <p className="text-muted-foreground">No posts yet.</p>}
        {posts.map((p) => (
          <div key={p.id} data-testid={`post-manage-${p.id}`} className="bg-card rounded-2xl border border-border p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover" />}
              <div>
                <div className="flex items-center gap-2"><h3 className="font-semibold text-lg">{p.title}</h3><span className={`text-xs px-2 py-0.5 rounded-full ${p.published ? "bg-secondary/20 text-secondary" : "bg-muted text-muted-foreground"}`}>{p.published ? "Published" : "Draft"}</span></div>
                <p className="text-sm text-muted-foreground max-w-md truncate">{p.excerpt}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(p)} className="rounded-lg p-2 hover:bg-muted"><Pencil className="w-5 h-5" /></button>
              <button onClick={() => del(p.id)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="w-5 h-5" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit Post" : "New Post"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="post-title-input" value={form.title} onChange={set("title")} /></div>
            <div><Label>Excerpt</Label><Textarea value={form.excerpt} onChange={set("excerpt")} className="min-h-[60px]" /></div>
            <div><Label>Cover Image URL</Label><Input value={form.cover_image || ""} onChange={set("cover_image")} /></div>
            <div><Label>Content (use ## for headings, ** for bold, - for lists)</Label><Textarea value={form.content} onChange={set("content")} className="min-h-[160px]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Author</Label><Input value={form.author} onChange={set("author")} /></div>
              <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={set("tags")} /></div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} /><Label>Published</Label></div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-post-btn" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground h-12 px-6 font-semibold disabled:opacity-60">{saving && <Loader2 className="w-5 h-5 animate-spin" />} Save Post</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
